import { useCallback, useMemo } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { ProductOffering } from './ConciergeServiceSection.types';
import type { ConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import { useInvitationOfferingsData, useSaveTheDateOfferingsData } from '@apps/card/routes/Dashboard/components/ConciergeServiceSection/ConciergeServiceSection.utils';

export const useProductOfferings = (): {
  productOfferings: Record<ConciergeServiceCategory, ReadonlyArray<ProductOffering>>;
  getConciergeCategoryFromDesignSetName: (designSetName: string) => ConciergeServiceCategory;
  getProductOfferingFromDesignSetName: (designSetName: string) => ProductOffering;
  getProductOfferingFromCategoryAndId: (category: ConciergeServiceCategory, id: string) => ProductOffering | undefined;
} => {
  const invitationOfferings = useInvitationOfferingsData();
  const saveTheDateOfferings = useSaveTheDateOfferingsData();

  const productOfferings = useMemo(() => {
    return {
      [StationeryTemplateCategoryEnum.invitation]: Object.values(invitationOfferings).sort(() => Math.random() - 0.5) as ReadonlyArray<ProductOffering>,
      [StationeryTemplateCategoryEnum.saveTheDate]: Object.values(saveTheDateOfferings).sort(() => Math.random() - 0.5) as ReadonlyArray<ProductOffering>,
    };
  }, [invitationOfferings, saveTheDateOfferings]);

  const getConciergeCategoryFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.keys(productOfferings).find(key =>
        productOfferings[key as ConciergeServiceCategory].some(offering => offering.identifier === designSetName),
      ) as ConciergeServiceCategory;
    },
    [productOfferings],
  );

  const getProductOfferingFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.values(productOfferings)
        .flat()
        .find(offering => offering.identifier === designSetName) as ProductOffering;
    },
    [productOfferings],
  );

  const getProductOfferingFromCategoryAndId = useCallback(
    (category: ConciergeServiceCategory, id: string) => {
      return productOfferings[category].find(offering => offering.id === id) as ProductOffering | undefined;
    },
    [productOfferings],
  );

  return {
    productOfferings,
    getConciergeCategoryFromDesignSetName,
    getProductOfferingFromDesignSetName,
    getProductOfferingFromCategoryAndId,
  };
};
